import React, { useState, useEffect, useContext } from "react";
import ModalHeader from "../Modal/ModalHeader";
import ModalFooter from "../Modal/ModalFooter";
import ModalMain from "../Modal/ModalMain";
import { Icon } from "@fluentui/react";
import themeContext from "../../context/theme/ThemeContext";
import { getAllBatchRequests, getBatchRequestDetails, extractFileName, downloadBatchRequestZipValidate, getBatchRequestZipUrl } from "../../service/StudyListService";
import "./DownloadBatchRequestsModal.css";
import toastContext from "../../context/toast/ToastContext";
import { API_BASE_URL, SERVICEURL, TOAST_TYPE } from "../../constants/Constants";
import studyReportContext from "../../context/studyreport/StudyReportContext";
import { color } from "d3";

const statusLabelMap = {
  QUEUED: "Queued",
  IN_PROGRESS: "In Progress",
  COMPLETED: "Completed",
  SUCCESS: "Success",
  EXPIRED: "Expired",
  FAILED: "Failed"
};

const reportTypeLabelMap = {
  PDF: "PDF",
  PDF_WITH_IMAGE: "PDF with Image",
  PDF_WITH_KEY_IMAGE: "PDF with Key Image"
};

const DownloadBatchRequestsModalTrigger = (props) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  //const [error, setError] = useState(null);
  const [infoModal, setInfoModal] = useState({ open: false, request: null, details: null, loadingDetails: false });
  const { getThemeColor, themeName } = useContext(themeContext);
  const { showToastv2, updateToastv2, list, setList } = useContext(toastContext);

  const {
      getConsistentDateFormat,
      getConsistentCustomDateFormat, 
    } = useContext(studyReportContext);

  const fetchBatchRequests = async (isInitial = false) => {
    if (isInitial) setLoading(true);
    try {
      const response = await getAllBatchRequests();
      if (response?.data) {
        const newData = response.data;
        setRequests(newData);

        const hasActive = newData.some(req => 
          ["QUEUED", "IN_PROGRESS", "Queued", "In Progress"].includes(req.status)
        );
        return hasActive;
      }
    } catch (error) {
      console.error("Error fetching batch requests", error);
    } finally {
      if (isInitial) setLoading(false);
    }
    return false;
  };

  console.log("request", requests);

  const fetchRequestDetails = async (requestId) => {
    setInfoModal(prev => ({ ...prev, loadingDetails: true }));
    
    try {
      const response = await getBatchRequestDetails(requestId);
      
      if (response && response.data) {
        console.log('Request details response:', response.data);
        setInfoModal(prev => ({ 
          ...prev, 
          details: response.data, 
          loadingDetails: false 
        }));
      } else {
        setInfoModal(prev => ({ ...prev, loadingDetails: false }));
      }
    } catch (error) {
      console.error('Error fetching request details:', error);
      setInfoModal(prev => ({ ...prev, loadingDetails: false }));
    }
  };

  // Handle download
  const handleDownload = async (requestId, fileName) => {
    try {
      const validateRes = await downloadBatchRequestZipValidate(requestId);
      if (validateRes?.status === 200) {
        // The browser downloads the zip itself, streaming it to disk. Reading it into the tab
        // first fails for multi-GB zips and left a 4-byte "null" file.
        const a = document.createElement("a");
        a.href = getBatchRequestZipUrl(requestId);
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        a.remove();
        showToastv2(TOAST_TYPE.SUCCESS, `${fileName || "Download"} started. See your browser's downloads.`);
      } else {
        showToastv2(TOAST_TYPE.ERROR, validateRes?.data?.message || "Validation failed.");
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      showToastv2(TOAST_TYPE.ERROR, "Some Error Occurred.");
    }
  };

  // Check if status allows download
  const canDownload = (status) => {
    const downloadableStatuses = ["COMPLETED", "SUCCESS", "Success"];
    return downloadableStatuses.includes(status);
  };

  useEffect(() => {
    let timer;

    const startPolling = () => {
      timer = setInterval(async () => {
        const shouldContinue = await fetchBatchRequests();
        if (!shouldContinue) clearInterval(timer);

    }, 5000);
    };

    if (props.showDownloadsModal) {
      fetchBatchRequests(true).then(() => {
        startPolling();
      });
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [props.showDownloadsModal]);

  const handleInfoModalOpen = (request) => {
    setInfoModal({ 
      open: true, 
      request: request, 
      details: null, 
      loadingDetails: false 
    });
    fetchRequestDetails(request.requestId || request.id);
  };

  const renderModalBody = () => (
    <div className="batch-modal-container" style={{ color: getThemeColor("primary-color") }}>
      {loading ? (
        <div className="batch-modal-loading-state" style={{ color: getThemeColor("primary-color") }}>
          <div>Loading...</div>
        </div>
      ) :!loading && requests.length === 0 ? (
            <div className="batch-modal-empty-state" style={{ color: getThemeColor("primary-color") }}>
              <div>No batch download requests found.</div>
            </div>

      ) : (
        <div className= {themeName != "light" ? "batch-modal-scroll-list" : "batch-modal-scroll-list-light"}>
          {requests.map((req, idx) => {
                const isExpired = req.status === "EXPIRED";            
                return (
                <div key={req.requestId} className="batch-modal-request-item">
                  <div className="batch-modal-status-row">
                    <div className="batch-modal-file-name" style={{ color: getThemeColor("downloads-heading") }}>
                      {req.displayName}
                    </div>
                    <div className="batch-modal-file-name" style={{ color: getThemeColor("downloads-heading") }}>
                      {getConsistentCustomDateFormat(req.createdOn)}
                    </div>
                  </div>

                  
                  <div className="batch-modal-status-row">
                    <div className="batch-modal-status-info">
                      <div 
                        className="batch-modal-status-text"
                        style={{ color: getThemeColor("downloads-sub-heading") }}
                      >
                        {statusLabelMap[req.status] || req.status}{req.status === "SUCCESS" && req.failedStudies !== 0 && " [Partial]"}
                        {req.status === "IN_PROGRESS" && req.progressPercentage != null && (
                          <div className="batch-modal-progress-bar">
                            <div 
                              className="batch-modal-progress-fill"
                              style={{ width: `${req.progressPercentage}%` }}
                            />
                          </div>
                        )}
                      </div>
                      {req.status === "IN_PROGRESS" && req.progressPercentage != null && (
                        <span className="batch-modal-progress-text" style={{ color: getThemeColor("primary-color") }}>
                          {req.progressPercentage}%
                        </span>
                      )}
                    </div>
                    <div className="batch-modal-action-buttons">

                       {!isExpired && req.expiresOn && canDownload(req.status) && (
                      <div className="batch-modal-expiry-row">
                        <div className="batch-modal-expiry-text" style={{ color: getThemeColor("downloads-heading") }}>
                          {/* <Icon iconName="Clock" styles={{ root: { fontSize: 12, marginRight: 4 } }} /> */}
                          <p style={{fontFamily: "Open Sans", fontSize: "14px", color: getThemeColor("downloads-heading")}}>
                            Link Expires At: {getConsistentCustomDateFormat(req.expiresOn)}
                          </p>
                          
                        </div>
                      </div>
                    )}

                  {isExpired && (
                      <div className="batch-modal-expiry-row">
                         <div className="batch-modal-expiry-text" style={{ color: getThemeColor("downloads-heading") }}>
                           <p style={{fontFamily: "Open Sans", fontSize: "14px", color: getThemeColor("downloads-heading")}}>
                               Link Expired
                             </p>
                          </div>
                      </div>
                    )}
                    {!isExpired && canDownload(req.status) && (
                      <span
                          className= {themeName != "light" ? "batch-modal-download-icon" : "batch-modal-download-icon-light"}
                             title="Download"
                             onClick={() => handleDownload(
                             req.requestId || req.id,
                             // OLD: extractFileName(req.downloadPath || req.displayName || req.fileName || req.name)
                             // NEW: downloadPath (absolute server path) is no longer returned by the API.
                             // displayName is already the bare file name, so extractFileName is a no-op on it.
                             extractFileName(req.displayName || req.fileName || req.name)
                              )}
                            >
                                  <Icon iconName="Installation" />
                                </span>
                              )}
                              <span
                                className= {themeName != "light" ? "batch-modal-info-icon" : "batch-modal-info-icon-light"}
                                title="View Details"
                                onClick={() => handleInfoModalOpen(req)}
                              >
                                <Icon iconName="Info" />
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
              </div>
              );
          

          const renderInfoModalBody = () => {
            const request = infoModal.request;
            const details = infoModal.details;
            
            if (!request) return null;

          return (
          <div className="info-modal-container" style={{ color: getThemeColor("primary-color") }}>
            {infoModal.loadingDetails ? (
              <div className="batch-modal-loading-state" style={{ color: getThemeColor("primary-color") }}>
                <div>Loading details...</div>
              </div>
            ) : details ? (
              <>
                {/* Summary Card */}
                <div className="info-modal-summary-card">
                  <div className="info-modal-summary-stats">
                    <div className="info-modal-stat-item">
                      <div className="info-modal-stat-number" style={{ color: getThemeColor("primary-color") }}>
                        {details.totalStudies || 0}
                      </div>
                      <div className="info-modal-stat-label" style={{ color: getThemeColor("primary-color") }}>
                        Total Selected
                      </div>
                    </div>
                    <div className="info-modal-stat-item">
                      <div className="info-modal-stat-number" style={{ color: getThemeColor("primary-color") }}>
                        {details.processed || 0}
                      </div>
                      <div className="info-modal-stat-label" style={{ color: getThemeColor("primary-color") }}>
                        Processed Successfully
                      </div>
                    </div>
                    <div className="info-modal-stat-item">
                      <div className="info-modal-stat-number" style={{ color: getThemeColor("primary-color") }}>
                        {details.failed || 0}
                      </div>
                      <div className="info-modal-stat-label" style={{ color: getThemeColor("primary-color") }}>
                        Failed
                      </div>
                    </div>
                  </div>
                </div>

            <div className="info-modal-study-table-container batch-modal-scroll-list"
              style={{ overflowY: "auto" }}>
              <table className="info-modal-study-table">
                <thead>
                  <tr>
                    <th>Patient Details</th>
                    <th>Report Type</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(details.studies || []).map((study, i) => (
                    <tr key={i}>
                      {/* Patient Details Column */}
                      <td>
                        <div style={{ color: getThemeColor("downloads-heading"), fontWeight: "bold" }}>
                          {study.patientName || "-"}
                        </div>
                        <div style={{ color: getThemeColor("downloads-heading") }}>
                          {study.patientId || "-"}
                        </div>
                      </td>

                      {/* Report Type Column */}
                      <td style={{ color: getThemeColor("downloads-heading") }}>
                        {reportTypeLabelMap[study.reportType] || "-"}
                      </td>

                      {/* Status Column */}
                      <td>
                        <div style={{ color: getThemeColor("downloads-heading") }}>
                          {statusLabelMap[study.status] || study.status}
                        </div>
                        {study.failureReason && (
                          <div style={{ color: getThemeColor("downloads-heading"), fontSize: "0.85em" }}>
                            {study.failureReason}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

          </>
          ) : (
          <div className="batch-modal-error-state">
            <div>Failed to load request details</div>
            <button 
              onClick={() => fetchRequestDetails(request.requestId || request.id)}
              className="batch-modal-retry-button"
            >
              Retry
            </button>
            </div>
            )}
          </div>
            );
          };

          const DefaultButtonList = [
            {
              title: "Close",
              onclick: () => props.setShowDownloadsModal(false),
            },
          ];

          const InfoDefaultButtonList = [
            {
              title: "Close",
              onclick: () => setInfoModal({ open: false, request: null, details: null, loadingDetails: false }),
            },
          ];

          return (
          <>
          {/* Main Batch Requests Modal */}
          {props.showDownloadsModal && (
          <ModalMain
          updatedModal={true}
          showModal={props.showDownloadsModal}
          modalDetails={{
            header: (
              <ModalHeader
                updatedModal={true}
                heading="Downloads"
                handleCancel={() => props.setShowDownloadsModal(false)}
              />
            ),
            body: renderModalBody(),
              footer: (
              <ModalFooter PrimaryButtonList={[]} DefaultButtonList={DefaultButtonList}>
                <div style={{ 
                  paddingLeft:"20px",
                  paddingRight:"8px" ,
                  paddingTop:"8px",
                  color: getThemeColor("downloads-heading"), 
                  fontSize: "12px",
                  textAlign: "left",
                  fontFamily : "Open Sans",
                  lineHeight:"normal"

                }}>
                 <strong>Note: </strong> All download links expire after 3 hrs. Users are requested to download again after link expiry. Request history will be cleared after 24 hrs.
                </div>
              </ModalFooter>
            ),
            width: "800px",
          }}
          
        />
        )}
      
        {/* Info Modal */}
        {infoModal.open && (
        <ModalMain
          updatedModal={true}
          showModal={infoModal.open}
          modalDetails={{
            header: (
              <ModalHeader
                updatedModal={true}
                heading="Request Details"
                handleCancel={() => setInfoModal({ open: false, request: null, details: null, loadingDetails: false })}
              />
            ),
            body: renderInfoModalBody(),
            footer: <ModalFooter PrimaryButtonList={[]} DefaultButtonList={InfoDefaultButtonList} />,
            width: "800px",
          }}
        />
        )}
    </>
  );
};

export default DownloadBatchRequestsModalTrigger;
